import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Building } from 'lucide-react';
import { Button } from '@/components/ui/button';

const LegalNoticePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/')} className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back to Home</span>
          </Button>
          <div className="flex items-center gap-2">
            <Building className="w-5 h-5 text-blue-600" />
            <span className="font-semibold text-gray-800">Legal Notice</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8 md:py-12">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-10">
          {/* Title */}
          <div className="text-center mb-12 pb-8 border-b border-gray-200">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
              <Building className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Legal Notice / Imprint</h1>
          </div>

          {/* Content Sections */}
          <div className="space-y-10">
            {/* Website Publisher */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Website Publisher</h2>
              <p className="text-gray-700 leading-relaxed">
                The SnowExchange platform is currently operated by a legally registered business entity. The operating entity may evolve over time, including in the context of incorporation, restructuring, or group formation. Such changes do not affect the validity of the information provided on this website nor the applicable contractual framework governing the use of the platform.
              </p>
            </section>

            {/* Platform Description */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Platform Description</h2>
              <p className="text-gray-700 leading-relaxed">
                SnowExchange is a B2B intermediation and matchmaking platform designed to facilitate professional collaborations between companies, consultants, freelancers, and service providers, notably in the context of ServiceNow-related projects and resource allocation. SnowExchange acts solely as a technology platform and does not operate as an employer, agency, or service provider.
              </p>
            </section>

            {/* Stage & Strategic Perspective */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Stage & Strategic Perspective</h2>
              <p className="text-gray-700 leading-relaxed">
                SnowExchange is an early-stage platform currently focused on building and scaling its core product and ecosystem adoption. The project may, in the future, explore strategic partnerships and investment opportunities aligned with its long-term vision.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Contact</h2>
              <div className="bg-gray-50 rounded-lg p-5 border border-gray-200 space-y-3">
                <div>
                  <p className="text-gray-600 text-sm mb-1">For general inquiries and user support:</p>
                  <a href="mailto:support@snow-exchange.com" className="text-blue-600 hover:underline font-medium">
                    support@snow-exchange.com
                  </a>
                </div>
                <div>
                  <p className="text-gray-600 text-sm mb-1">For legal or compliance-related matters:</p>
                  <a href="mailto:legal@snow-exchange.com" className="text-blue-600 hover:underline font-medium">
                    legal@snow-exchange.com
                  </a>
                </div>
              </div>
            </section>

            {/* Hosting Provider */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Hosting Provider</h2>
              <p className="text-gray-700 leading-relaxed">
                The SnowExchange platform is hosted by a third-party cloud and application hosting provider selected for its reliability, scalability, and security standards. Hosting infrastructure and providers may evolve over time without prior notice.
              </p>
            </section>

            {/* Intellectual Property */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Intellectual Property</h2>
              <p className="text-gray-700 leading-relaxed">
                All content, trademarks, logos, and elements available on this website, excluding user-generated content, are the property of SnowExchange or its licensors and are protected by applicable intellectual property laws.
              </p>
            </section>

            {/* Applicable Documents */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Applicable Documents</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                The use of the SnowExchange platform is governed by the following documents, which should be read together:
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/terms" className="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 transition-colors text-sm font-medium">
                  Terms of Use
                </Link>
                <Link to="/privacy" className="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 transition-colors text-sm font-medium">
                  Privacy Policy
                </Link>
                <Link to="/ai-disclaimer" className="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 transition-colors text-sm font-medium">
                  AI Disclaimer
                </Link>
              </div>
            </section>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 pt-8 mt-12 text-center">
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} SnowExchange. All rights reserved.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LegalNoticePage;
